import type { Organization, VolunteerImportResult } from '@parinaam-vms/shared';
import { apiDownload, apiGet, apiPatch, apiPost, toQueryString } from './client';

/** A `type`, not an `interface`, so it can be passed to `toQueryString`. */
export type OrganizationListQuery = {
  search?: string;
  include_inactive?: boolean;
};

export interface CreateOrganizationBody {
  name: string;
  city?: string | null;
  notes?: string | null;
}

/**
 * Unpaginated: this is a pick-list staff choose from, not a directory they
 * navigate. Retired partners are excluded unless asked for.
 */
/** The shape the public registration form needs: a label and an id. */
export interface OrganizationOption {
  organization_id: string;
  name: string;
  city: string | null;
}

/**
 * The partner list for the public form.
 *
 * A different endpoint from `fetchOrganizations`, not the same one with fewer
 * fields: that one is the staff registry and is refused to anybody not signed
 * in, which is why the field on /register was an empty dropdown.
 */
export function fetchOrganizationOptions(): Promise<OrganizationOption[]> {
  return apiGet<OrganizationOption[]>('/organizations/options');
}

export function fetchOrganizations(params: OrganizationListQuery = {}): Promise<Organization[]> {
  return apiGet<Organization[]>(`/organizations${toQueryString(params)}`);
}

export function createOrganization(body: CreateOrganizationBody): Promise<Organization> {
  return apiPost<Organization>('/organizations', body);
}

/**
 * Bulk-import a corporate partner's volunteers from a CSV (item 8).
 *
 * The file is read to text in the browser and posted as one string, so no
 * multipart upload is involved. The result is a per-row report, because partial
 * success is the normal case.
 */
/**
 * A CSV as text, or a workbook as base64 — exactly one, and the caller decides
 * which because it is the one that knows how it read the file.
 */
export type ImportPayload = { csv: string } | { xlsx: string };

export function importOrganizationVolunteers(
  organizationId: string,
  payload: ImportPayload
): Promise<VolunteerImportResult> {
  return apiPost<VolunteerImportResult>(
    `/organizations/${organizationId}/volunteers/import`,
    payload
  );
}

/** The blank workbook to send a partner. Saved straight to disk. */
export function downloadImportTemplate(): Promise<void> {
  return apiDownload(
    '/organizations/import-template',
    'parinaam-volunteer-import-template.xlsx'
  );
}

/**
 * Renaming a partner renames every corporate volunteer record pointing at it —
 * by database trigger, not by this call, because NocoDB renames without passing
 * through the API at all.
 */
export function updateOrganization(
  id: string,
  body: Partial<CreateOrganizationBody> & { is_active?: boolean }
): Promise<Organization> {
  return apiPatch<Organization>(`/organizations/${id}`, body);
}
